import React, { useEffect, useRef, useState } from "react";
import "../assets/css/embed-video.css";

function buildEmbedUrl(src) {
  try {
    const url = new URL(src);

    if (url.hostname.includes("youtube.com")) {
      const videoId = url.pathname.split("/embed/")[1]?.split("/")[0];
      if (!videoId) return src;

      const params = new URLSearchParams(url.search);
      params.set("autoplay", "1");
      params.set("mute", "1");
      params.set("playsinline", "1");
      params.set("rel", "0");
      params.set("modestbranding", "1");
      params.set("loop", "1");
      params.set("playlist", videoId);

      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }

    if (url.hostname.includes("vimeo.com")) {
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("muted", "1");
      url.searchParams.set("loop", "1");
      return url.toString();
    }

    return src;
  } catch {
    return src;
  }
}

function isNearViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.top < window.innerHeight + 250 &&
    rect.bottom > -250
  );
}

export default function EmbedVideo({ src, title, className = "", minHeight }) {
  const containerRef = useRef(null);
  const [iframeSrc, setIframeSrc] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let loaded = false;

    const load = () => {
      if (loaded) return;
      loaded = true;
      setIframeSrc(buildEmbedUrl(src));
    };

    const tryLoad = () => {
      if (loaded) return false;
      if (isNearViewport(el)) {
        load();
        return true;
      }
      return false;
    };

    if (tryLoad()) {
      return undefined;
    }

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRect.width > 0 &&
          entry.intersectionRect.height > 0
        ) {
          load();
          intersectionObserver.disconnect();
        }
      },
      { rootMargin: "250px 0px", threshold: 0.01 }
    );

    intersectionObserver.observe(el);

    const resizeObserver = new ResizeObserver(() => {
      if (tryLoad()) {
        intersectionObserver.disconnect();
      }
    });

    resizeObserver.observe(el);

    return () => {
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [src]);

  const classes = [
    "embed-video",
    minHeight ? "embed-video--fixed" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style = minHeight
    ? {
        "--embed-min-height":
          typeof minHeight === "number" ? `${minHeight}px` : minHeight,
      }
    : undefined;

  return (
    <div ref={containerRef} className={classes} style={style}>
      {iframeSrc ? (
        <iframe
          src={iframeSrc}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : null}
    </div>
  );
}
